import { fetchDocCategoriesByIds } from 'components/DocCategorySelect/utils';
import { Template, TemplateData } from 'components/documentTemplates/types';
import { buildConditionsList } from 'components/documentTemplates/utils';

import graph from './graph';
import io from './io';

const TEMPLATE_MEMBER_FRAGMENT = `
    id
    type
    role {
        id
        user {
            email
            firstName
            secondName
            lastName
        }
    }
    group {
        id
        name
        members {
            id
            role {
                id
                user {
                    email
                    firstName
                    secondName
                    lastName
                }
            }
        }
    }
`;

const TEMPLATE_AUTOMATION_FRAGMENT = `
    id
    conditions
    status
`;

const getDocCategoryValuesFromTemplateAutomation = (obj) => {
    let values = [];

    if (Array.isArray(obj)) {
        for (const item of obj) {
            values = values.concat(
                getDocCategoryValuesFromTemplateAutomation(item),
            );
        }
    } else if (typeof obj === 'object' && obj !== null) {
        for (const key in obj) {
            if (
                key === 'eq' &&
                Array.isArray(obj[key]) &&
                obj[key][0] === '#document_category'
            ) {
                values.push(obj[key][1]);
            } else {
                values = values.concat(
                    getDocCategoryValuesFromTemplateAutomation(obj[key]),
                );
            }
        }
    }

    return values;
};

const getTemplatesWithDocCategoryDetails = async (
    documentTemplates: Template[],
) => {
    const docCategoriesIds: (number | string)[] | [] = [];

    documentTemplates.map((template) =>
        template.automation.map((auto) => {
            const conditionsList = buildConditionsList(auto.conditions);

            conditionsList.forEach((condition) => {
                if (
                    condition[0] === '#document_category' &&
                    !docCategoriesIds.includes(condition[1])
                ) {
                    docCategoriesIds.push(condition[1]);
                }
            });
        }),
    );

    if (!docCategoriesIds.length) {
        return documentTemplates;
    }

    const docCategoriesData = await fetchDocCategoriesByIds({
        ids: docCategoriesIds,
    });

    const documentTemplatesWithDocCategoryDetails = documentTemplates.map(
        (template) => {
            const updatedAutomation = template.automation?.map(
                (automationItem) => {
                    const docCategoryValues = getDocCategoryValuesFromTemplateAutomation(
                        automationItem,
                    );
                    if (docCategoryValues.length) {
                        return {
                            ...automationItem,
                            docCategoriesDetails: docCategoriesData?.documentCategories.filter(
                                (item) =>
                                    docCategoryValues.includes(String(item.id)),
                            ),
                        };
                    }

                    return automationItem;
                },
            );

            return {
                ...template,
                automation: updatedAutomation,
            };
        },
    );

    return documentTemplatesWithDocCategoryDetails;
};

export async function getDocumentTemplates(): Promise<Template[]> {
    const { documentAutomationTemplates } = await graph.get(/* GraphQL */ `{
        documentAutomationTemplates {
            id
            name
            isActive
            reviewSettings
            signersSettings
            viewersSettings
            fieldsSettings
            tagsSettings
            assignedTo {
                id
                status
                user {
                    id
                    email
                    firstName
                    lastName
                }
            }
            tags {
                id
                name
            }
            reviewers {
                ${TEMPLATE_MEMBER_FRAGMENT}
            }
            signers {
                ${TEMPLATE_MEMBER_FRAGMENT}
            }
            viewerRoles {
                id
                user {
                    email
                    firstName
                    secondName
                    lastName
                }
            }
            automation {
                ${TEMPLATE_AUTOMATION_FRAGMENT}
            }
        }
    }`);

    const documentTemplatesWithDocCategoryDetails = await getTemplatesWithDocCategoryDetails(
        documentAutomationTemplates,
    );

    return documentTemplatesWithDocCategoryDetails;
}

export async function createDocumentTemplate(data: TemplateData) {
    return await io.post('/internal-api/documents/templates', data);
}

export async function updateDocumentTemplate(
    templateId: string,
    data: TemplateData,
) {
    return await io.request(
        'PUT',
        `/internal-api/documents/templates/${templateId}`,
        data,
    );
}

export async function activateDocumentTemplate(
    templateId: string,
    data: TemplateData,
) {
    return await io.request(
        'PATCH',
        `/internal-api/documents/templates/${templateId}`,
        data,
    );
}

export async function deleteDocumentTemplate(templateId: string) {
    return await io.request(
        'DELETE',
        `/internal-api/documents/templates/${templateId}`,
    );
}

export async function activateDocumentTemplateAutomation(
    automationId: string,
    isActive: boolean,
) {
    return await io.request(
        'PATCH',
        `/internal-api/documents/templates/automation/${automationId}`,
        { is_active: isActive },
    );
}
